import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { Outlet, useLocation, useSearchParams } from 'react-router-dom'
import moment from 'moment'
import { Dropdown } from 'react-bootstrap'
import ButtonGroup from '@components/common/ButtonGroup/ButtonGroup.component'
import Button from '@components/common/Button/Button.component'
import ToggleButton from '@components/common/ToggleButton/ToggleButton.component'
import CalendarToggle from '@components/pages/Appointments/CalendarToggle.component'
import '@components/pages/Dashboard/DashboardLayout.style.css'
import DashboardOverview from '@components/pages/Dashboard/Overview/DashboardOverview.component'
import DashboardHuddle from '@components/pages/Dashboard/HuddleTab/DashboardHuddle.component'
import DashboardOperations from '@components/pages/Dashboard/Operations/DashboardOperations.component'
import DashboardFinance from '@components/pages/Dashboard/Finance/DashboardFinance.component'
import DashboardPerformance from '@components/pages/Dashboard/Performance/DashboardPerformance.component'
import { DashboardLayoutEditProvider } from '@components/pages/Dashboard/DashboardLayoutEditContext'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  DashboardRange,
  DashboardTab,
  HuddleDay,
} from '@constants/Dashboard/dashboard.enums'
import { toDashboardSentenceCase } from '@utils/dashboardText.util'
import {
  DASHBOARD_VIEW_ROLE_TO_PERSONA,
  resolveSignedInDashboardViewRole,
} from '../../types/dashboardViewRole.types'
import {
  DASHBOARD_SECTIONS_BY_TAB,
  DATE_RANGE_TO_RANGE_KEY,
  applyHiddenSectionsToConfig,
  buildHiddenSectionsFromConfig,
} from '@constants/Dashboard/dashboardSettingsApply.constants'
import { omitTemporarilyHiddenSections } from '@constants/Dashboard/huddleSectionVisibility.constants'
import { useDashboardSettings } from '@hooks/dashboard/useDashboardSettings'
import { DashboardRefreshProvider } from '@context/DashboardRefreshContext'
import { DashboardFetchWaveProvider } from '@context/DashboardFetchWaveContext'
import { saveDashboardSettings } from '@services/api/dashboard/dashboard-settings.api'
import { buildDefaultDashboardConfigs } from '@components/pages/Settings/Analytics/Dashboard/dashboardSettings.constants'
import { parseUser } from '@services/api'
import { useAppSelector } from '@store/hooks'
import {
  consumeDashboardScrollSnapshot,
  parseDashboardHuddleDay,
  parseDashboardIndexParams,
  patchDashboardIndexSearchParams,
  resolveDashboardTabFromDetailSearch,
  restoreDashboardScrollWithRetry,
  isDashboardHuddleDayTab,
  isDashboardIndexTab,
  type DashboardHuddleDayTab,
  type DashboardIndexTab,
} from '@utils/dashboardReturnNavigation.util'

const CALENDAR_DATE_FORMAT = 'MMMM D, YYYY'

/** Persona default range tab (weekly → 1W) used when the URL has no `range`. */
function seedPersonaDefaultRange(): RangeKey {
  const view = resolveSignedInDashboardViewRole(
    undefined,
    parseUser()?.user_role,
  )
  const personaKey = DASHBOARD_VIEW_ROLE_TO_PERSONA[view]
  const defaults = buildDefaultDashboardConfigs()
  const seeded = personaKey ? defaults[personaKey]?.display.dateRange : 'weekly'
  return (DATE_RANGE_TO_RANGE_KEY[seeded ?? 'weekly'] ??
    DashboardRange.OneWeek) as RangeKey
}

const DASHBOARD_TABS = [
  { label: 'Overview', value: DashboardTab.Overview },
  { label: 'Huddle', value: DashboardTab.Huddle },
  { label: 'Operations', value: DashboardTab.Operations },
  { label: 'Finance', value: DashboardTab.Finance },
  { label: 'Performance', value: DashboardTab.Performance },
] as const

const RANGE_TABS = [
  { label: '1D', value: DashboardRange.OneDay },
  { label: '1W', value: DashboardRange.OneWeek },
  { label: '1M', value: DashboardRange.OneMonth },
  { label: '1Y', value: DashboardRange.OneYear },
  { label: 'YTD', value: DashboardRange.YearToDate },
] as const

const HUDDLE_DAY_TABS = [
  { label: 'Yesterday', value: HuddleDay.Yesterday },
  { label: 'Today', value: HuddleDay.Today },
  { label: 'Tomorrow', value: HuddleDay.Tomorrow },
] as const

/** Overview, Finance, and Performance use three waves; Huddle and Operations use two. */
function dashboardFetchWaveCount(tab: DashboardTab): number {
  if (tab === DashboardTab.Huddle || tab === DashboardTab.Operations) return 2
  return 3
}

/** Section titles for a dashboard tab, omitting titles hidden until a real API exists. */
function layoutSectionsForTab(tab: DashboardTab): string[] {
  return omitTemporarilyHiddenSections(
    tab,
    DASHBOARD_SECTIONS_BY_TAB[tab] ?? [],
  )
}

function DashboardPage(): React.ReactElement {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const isDashboardSubpage = location.pathname.startsWith('/dashboard/')
  const wasSubpageRef = useRef(isDashboardSubpage)
  const signedInWorkRole = useAppSelector(
    (state) => state.chatInternal.loggedInUser?.provider_profile?.work_role,
  )
  // Seed tab/day/range/date from the URL on first paint so deep links (e.g.
  // ?tab=finance) never mount Overview first and abort that request wave.
  const [activeLeftTab, setActiveLeftTab] = useState<DashboardTab>(() =>
    resolveDashboardTabFromDetailSearch(searchParams, DashboardTab.Overview),
  )
  const [huddleDayTab, setHuddleDayTab] = useState<HuddleDay>(
    () =>
      parseDashboardHuddleDay(searchParams, HuddleDay.Today) ?? HuddleDay.Today,
  )
  const [date, setDate] = useState<string>(() => {
    const today = moment().format(CALENDAR_DATE_FORMAT)
    return (
      parseDashboardIndexParams(searchParams, { date: today }).date ?? today
    )
  })
  const [editMode, setEditMode] = useState<boolean>(false)
  const [showSettingsDropdown, setShowSettingsDropdown] =
    useState<boolean>(false)
  const [hiddenSections, setHiddenSections] = useState<
    Record<string, Set<string>>
  >({})
  const viewRole = resolveSignedInDashboardViewRole(
    signedInWorkRole,
    parseUser()?.user_role,
  )

  // Clinic-scoped dashboard settings (persona-keyed, defaults merged in).
  const {
    settings: dashboardSettings,
    clinicId,
    reload: reloadSettings,
  } = useDashboardSettings()
  const activePersonaConfig = useMemo(() => {
    const personaKey = DASHBOARD_VIEW_ROLE_TO_PERSONA[viewRole]
    return personaKey ? dashboardSettings[personaKey] : undefined
  }, [dashboardSettings, viewRole])

  // URL `range` wins; otherwise persona default (weekly → 1W).
  const [activeRightTab, setActiveRightTab] = useState<string>(() => {
    const personaDefault = seedPersonaDefaultRange()
    return (
      parseDashboardIndexParams(searchParams, { range: personaDefault })
        .range ?? personaDefault
    )
  })

  // Tracks whether the dashboard's own hide/show editor has diverged from the
  // saved settings, so layout edits can be persisted back on close (§7.5).
  const [layoutDirty, setLayoutDirty] = useState(false)

  // Apply the active persona's saved settings: hide sections for disabled
  // widgets / display prefs, and default the period range tab. Re-runs when the
  // persona or the loaded settings change; manual edits made after this
  // persist until the next settings reload.
  useEffect(() => {
    if (!activePersonaConfig) return
    setHiddenSections(buildHiddenSectionsFromConfig(activePersonaConfig))
    setLayoutDirty(false)
    if (searchParams.has('range')) return
    const mappedRange =
      DATE_RANGE_TO_RANGE_KEY[activePersonaConfig.display.dateRange]
    if (mappedRange) {
      setActiveRightTab((prev) => (prev === mappedRange ? prev : mappedRange))
    }
  }, [activePersonaConfig, searchParams])

  const hasIndexQueryParams = useMemo(
    () =>
      ['tab', 'day', 'range', 'date', 'context'].some((key) =>
        searchParams.has(key),
      ),
    [searchParams],
  )

  useEffect(() => {
    if (isDashboardSubpage || !hasIndexQueryParams) return
    const parsed = parseDashboardIndexParams(searchParams, {
      tab: activeLeftTab,
      day: huddleDayTab,
      range: activeRightTab as RangeKey,
      date,
    })
    setActiveLeftTab(parsed.tab)
    if (parsed.day) setHuddleDayTab(parsed.day)
    if (parsed.range) setActiveRightTab(parsed.range)
    if (parsed.date) setDate(parsed.date)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apply URL snapshot only when search changes
  }, [isDashboardSubpage, hasIndexQueryParams, location.search])

  useEffect(() => {
    const wasSubpage = wasSubpageRef.current
    wasSubpageRef.current = isDashboardSubpage
    if (!wasSubpage || isDashboardSubpage) return
    const snapshot = consumeDashboardScrollSnapshot()
    if (!snapshot) return
    // Wait for tab content to layout before restoring scroll.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        restoreDashboardScrollWithRetry(snapshot)
      })
    })
  }, [isDashboardSubpage])

  const syncIndexUrl = useCallback(
    (overrides: {
      tab?: DashboardIndexTab
      day?: DashboardHuddleDayTab
      range?: RangeKey
      date?: string
    }) => {
      if (isDashboardSubpage) return
      setSearchParams(
        patchDashboardIndexSearchParams(searchParams, {
          tab: activeLeftTab,
          ...(activeLeftTab === DashboardTab.Huddle
            ? { day: huddleDayTab }
            : {}),
          range: activeRightTab as RangeKey,
          date,
          ...overrides,
        }),
        { replace: true },
      )
    },
    [
      activeLeftTab,
      activeRightTab,
      date,
      huddleDayTab,
      isDashboardSubpage,
      searchParams,
      setSearchParams,
    ],
  )

  const handleLeftTabChange = useCallback(
    (tab: string) => {
      if (!isDashboardIndexTab(tab)) return
      setActiveLeftTab(tab)
      syncIndexUrl({ tab })
    },
    [syncIndexUrl],
  )

  const handleHuddleDayChange = useCallback(
    (day: HuddleDay) => {
      setHuddleDayTab(day)
      syncIndexUrl({ day })
    },
    [syncIndexUrl],
  )

  const handleRangeChange = useCallback(
    (range: string) => {
      setActiveRightTab(range)
      syncIndexUrl({ range: range as RangeKey })
    },
    [syncIndexUrl],
  )

  // Periodic refetch: bump refresh key so period hooks refetch in place
  // without remounting (and wiping) every chart.
  const refreshMinutes =
    Number(activePersonaConfig?.display.refreshInterval) || 0
  const [refreshNonce, setRefreshNonce] = useState(0)
  useEffect(() => {
    if (refreshMinutes <= 0) return
    const id = window.setInterval(
      () => setRefreshNonce((n) => n + 1),
      refreshMinutes * 60_000,
    )
    return () => window.clearInterval(id)
  }, [refreshMinutes])

  const visibleDashboardTabs = useMemo(() => {
    return DASHBOARD_TABS.filter((tab) => {
      const hidden = hiddenSections[tab.value] ?? new Set<string>()
      return layoutSectionsForTab(tab.value).some((name) => !hidden.has(name))
    })
  }, [hiddenSections])

  useEffect(() => {
    if (visibleDashboardTabs.length === 0) return
    if (visibleDashboardTabs.some((tab) => tab.value === activeLeftTab)) return
    handleLeftTabChange(visibleDashboardTabs[0].value)
  }, [activeLeftTab, handleLeftTabChange, visibleDashboardTabs])

  const toggleSection = useCallback((tab: string, name: string) => {
    setHiddenSections((prev) => {
      const next = new Set(prev[tab] ?? [])
      if (next.has(name)) {
        next.delete(name)
      } else {
        next.add(name)
      }
      return { ...prev, [tab]: next }
    })
    setLayoutDirty(true)
  }, [])

  // Persist hide/show edits made from the dashboard back through the settings
  // PUT endpoint so the settings page and the live dashboard stay in sync
  // (§7.5). Saved onto the signed-in role's persona.
  const hiddenSectionsRef = useRef(hiddenSections)
  hiddenSectionsRef.current = hiddenSections
  const layoutDirtyRef = useRef(layoutDirty)
  layoutDirtyRef.current = layoutDirty

  const persistLayout = useCallback((): void => {
    if (!layoutDirtyRef.current || !clinicId) return
    const personaKey = DASHBOARD_VIEW_ROLE_TO_PERSONA[viewRole]
    if (!personaKey) return
    const currentConfig = dashboardSettings[personaKey]
    if (!currentConfig) return

    setLayoutDirty(false)
    const updatedConfig = applyHiddenSectionsToConfig(
      currentConfig,
      hiddenSectionsRef.current,
    )
    saveDashboardSettings({
      clinicId,
      settings: { ...dashboardSettings, [personaKey]: updatedConfig },
    })
      .then(() => reloadSettings())
      .catch(() => setLayoutDirty(true))
  }, [clinicId, viewRole, dashboardSettings, reloadSettings])

  useEffect(() => {
    if (showSettingsDropdown) return
    persistLayout()
  }, [showSettingsDropdown, persistLayout])

  const handleDateChange = useCallback(
    (dateString: string) => {
      if (moment(dateString).isSame(date, 'day')) return
      setDate(dateString)
      syncIndexUrl({ date: dateString })
    },
    [date, syncIndexUrl],
  )

  const handlePrevDay = (): void => {
    const nextDate = moment(date).subtract(1, 'days').format('MMMM D, YYYY')
    setDate(nextDate)
    syncIndexUrl({ date: nextDate })
  }

  const handleNextDay = (): void => {
    const nextDate = moment(date).add(1, 'days').format('MMMM D, YYYY')
    setDate(nextDate)
    syncIndexUrl({ date: nextDate })
  }

  const layoutSectionsList = useMemo(
    () => layoutSectionsForTab(activeLeftTab),
    [activeLeftTab],
  )

  const settingsDropdown = (
    <Dropdown
      show={showSettingsDropdown}
      onToggle={(isOpen) => setShowSettingsDropdown(!!isOpen)}
      autoClose="outside"
      align="start"
      className="dashboard-settings-dropdown"
    >
      <Dropdown.Toggle
        as={Button}
        variant=""
        className={`button-secondary dashboard-gear-button ${editMode ? 'dashboard-gear-button--active' : ''}`}
        aria-label="Dashboard settings"
      >
        <i className="mgc_settings_5_fill" />
      </Dropdown.Toggle>
      <Dropdown.Menu className="dashboard-settings-menu">
        <div className="dashboard-settings-menu-header">
          <span className="dashboard-settings-menu-title">Layout</span>
          <ToggleButton
            id="dashboard-edit-mode-switch"
            type="switch"
            name="editMode"
            checked={editMode}
            onChange={(e) =>
              setEditMode((e.target as HTMLInputElement).checked)
            }
            label="Enable drag & resize"
          />
        </div>
        <div className="dashboard-settings-sections">
          <span className="dashboard-settings-sections-title">
            {DASHBOARD_TABS.find((t) => t.value === activeLeftTab)?.label ??
              activeLeftTab}
          </span>
          <ul className="dashboard-settings-sections-list">
            {layoutSectionsList.map((name) => {
              const hidden = (hiddenSections[activeLeftTab] ?? new Set()).has(
                name,
              )
              return (
                <li
                  key={name}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    toggleSection(activeLeftTab, name)
                  }}
                  className={
                    hidden ? 'dashboard-settings-list-item--hidden' : ''
                  }
                >
                  <span>{toDashboardSentenceCase(name)}</span>
                  <i className={hidden ? 'mgc_check_line' : 'mgc_check_fill'} />
                </li>
              )
            })}
          </ul>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  )

  return (
    <div className="dashboard-container is-ready">
      {!isDashboardSubpage && (
        <div className="dashboard-header-stack">
          <header className="dashboard-header">
            <div className="dashboard-header-left">
              <ButtonGroup
                buttons={
                  visibleDashboardTabs as unknown as {
                    label: string
                    value: string
                  }[]
                }
                activeTab={activeLeftTab}
                onButtonClick={handleLeftTabChange}
              />
            </div>
            <div className="dashboard-header-right">{settingsDropdown}</div>
          </header>
          <div
            className={`dashboard-toolbar${activeLeftTab === DashboardTab.Huddle ? ' dashboard-toolbar--huddle' : ''}`}
          >
            {activeLeftTab === DashboardTab.Huddle ? (
              <ButtonGroup
                buttons={
                  HUDDLE_DAY_TABS as unknown as {
                    label: string
                    value: string
                  }[]
                }
                activeTab={huddleDayTab}
                onButtonClick={(v) => {
                  if (isDashboardHuddleDayTab(v)) handleHuddleDayChange(v)
                }}
                className="dashboard-huddle-day-tabs"
              />
            ) : (
              <>
                <div className="dashboard-toolbar-left">
                  <CalendarToggle
                    activeTab="day"
                    date={date}
                    startDate={date}
                    endDate={date}
                    onDateChange={handleDateChange}
                  />
                  <div className="dashboard-date-chevron-group">
                    <button
                      type="button"
                      className="unstyled-button ti ti-chevron-left"
                      onClick={handlePrevDay}
                      aria-label="Previous day"
                    />
                    <button
                      type="button"
                      className="unstyled-button ti ti-chevron-right"
                      onClick={handleNextDay}
                      aria-label="Next day"
                    />
                  </div>
                </div>
                <ButtonGroup
                  buttons={
                    RANGE_TABS as unknown as { label: string; value: string }[]
                  }
                  activeTab={activeRightTab}
                  onButtonClick={handleRangeChange}
                  className="dashboard-right-tabs"
                />
              </>
            )}
          </div>
        </div>
      )}
      <main className="dashboard-main">
        <DashboardRefreshProvider refreshKey={refreshNonce}>
          <DashboardLayoutEditProvider value={editMode}>
            {!isDashboardSubpage ? (
              <DashboardFetchWaveProvider
                resetKey={`${activeLeftTab}|${date}|${activeRightTab}|${huddleDayTab}`}
                waveCount={dashboardFetchWaveCount(activeLeftTab)}
              >
                <div className="dashboard-tab-content">
                  {activeLeftTab === DashboardTab.Overview ? (
                    <DashboardOverview
                      date={date}
                      onDateChange={handleDateChange}
                      onPrevDay={handlePrevDay}
                      onNextDay={handleNextDay}
                      editMode={editMode}
                      hiddenSections={
                        hiddenSections[DashboardTab.Overview] ?? new Set()
                      }
                      range={activeRightTab as RangeKey}
                    />
                  ) : activeLeftTab === DashboardTab.Huddle ? (
                    <DashboardHuddle
                      activeDayTab={huddleDayTab}
                      editMode={editMode}
                      hiddenSections={
                        hiddenSections[DashboardTab.Huddle] ?? new Set()
                      }
                    />
                  ) : activeLeftTab === DashboardTab.Operations ? (
                    <DashboardOperations
                      editMode={editMode}
                      hiddenSections={
                        hiddenSections[DashboardTab.Operations] ?? new Set()
                      }
                      date={date}
                      range={activeRightTab as RangeKey}
                    />
                  ) : activeLeftTab === DashboardTab.Finance ? (
                    <DashboardFinance
                      editMode={editMode}
                      hiddenSections={
                        hiddenSections[DashboardTab.Finance] ?? new Set()
                      }
                      date={date}
                      range={activeRightTab as RangeKey}
                    />
                  ) : activeLeftTab === DashboardTab.Performance ? (
                    <DashboardPerformance
                      editMode={editMode}
                      hiddenSections={
                        hiddenSections[DashboardTab.Performance] ?? new Set()
                      }
                      date={date}
                      range={activeRightTab as RangeKey}
                    />
                  ) : null}
                </div>
              </DashboardFetchWaveProvider>
            ) : null}
            {isDashboardSubpage ? <Outlet /> : null}
          </DashboardLayoutEditProvider>
        </DashboardRefreshProvider>
      </main>
    </div>
  )
}

export default DashboardPage
